/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import { formatCurrency } from '@sportninja/common/currency'
import { resolveCurrencyCode } from '@sportninja/common/currency/constants'

interface PriceProps {
  /** Amount in minor units (e.g. 1250 = $12.50) */
  amount: number
  /** ISO 4217 code (e.g. 'CAD') or numeric API ID (e.g. 1) */
  currency: string | number
  /** BCP 47 locale override */
  locale?: string
  /** Omit decimals for whole amounts */
  compact?: boolean
  /** Show ISO code instead of symbol (e.g. "CAD 12.50") */
  showCode?: boolean
  /** CSS class name */
  className?: string
}

/**
 * Display a formatted price.
 *
 * @example
 * <Price amount={1250} currency="USD" />
 * // renders: <span>$12.50</span>
 *
 * <Price amount={1200} currency={1} compact />
 * // renders: <span>CA$12</span>
 */
const Price = ({
  amount,
  currency,
  locale,
  compact = false,
  showCode = false,
  className,
}: PriceProps) => {
  const currencyCode = useMemo(() => {
    try {
      return resolveCurrencyCode(currency)
    } catch {
      return typeof currency === 'string' ? currency.toUpperCase() : 'CAD'
    }
  }, [currency])

  const formatted = useMemo(
    () =>
      formatCurrency({
        amount,
        currencyCode,
        locale,
        compact,
        showCode,
      }),
    [amount, currencyCode, locale, compact, showCode]
  )

  return (
    <span
      className={className}
      data-currency={currencyCode}
      data-amount={amount}
    >
      {formatted}
    </span>
  )
}

export default Price
